/**
 * DeactivateNodeModal (S1.1)
 *
 * Deactivate-with-replacement flow (no hard delete exists). Follows the
 * category ReplaceCategoryModal pattern: warning + replacement Select +
 * MANDATORY change comment. Whether products are assigned is decided by the
 * SERVER (REPLACEMENT_REQUIRED) — on that answer the modal escalates and
 * requires a replacement category, keeping this slice decoupled from the
 * in-flight S1.2 product schema.
 */

import { useEffect, useMemo, useState } from 'react';
import { Alert, App, Input, Modal, Select, Typography, theme } from 'antd';
import { useTranslation } from 'react-i18next';
import { useFormStyles } from '@/styles/formStyles';
import { controlHeightTokens } from '@/styles/tokens';
import {
  buildBreadcrumb,
  extractGuardError,
  subtreeIds,
  useTaxonomyMutations,
} from '@/hooks/useTaxonomy';
import type { ProductCategory } from '@/types/api/productCategory';

const { Text } = Typography;

export interface DeactivateNodeModalProps {
  /** Node being deactivated (modal renders only while set). */
  node: ProductCategory | null;
  nodes: ProductCategory[];
  onClose: () => void;
  /** Called after a successful deactivation (page refreshes the tree). */
  onDeactivated: () => void;
}

const DeactivateNodeModal = ({ node, nodes, onClose, onDeactivated }: DeactivateNodeModalProps) => {
  const { t, i18n } = useTranslation(['taxonomy', 'common']);
  const { token } = theme.useToken();
  const { message } = App.useApp();
  const formStyles = useFormStyles();
  const { deactivateNode } = useTaxonomyMutations();
  const isZh = i18n.language.startsWith('zh');

  const [replacementId, setReplacementId] = useState<string | undefined>(undefined);
  const [comment, setComment] = useState('');
  const [replacementRequired, setReplacementRequired] = useState(false);
  const [showCommentError, setShowCommentError] = useState(false);
  const [showReplacementError, setShowReplacementError] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (node) {
      setReplacementId(undefined);
      setComment('');
      setReplacementRequired(false);
      setShowCommentError(false);
      setShowReplacementError(false);
    }
  }, [node]);

  // Active nodes OUTSIDE the deactivated subtree (mirrors guardDeactivate)
  const replacementOptions = useMemo(() => {
    if (!node) return [];
    const excluded = subtreeIds(nodes, node.id);
    const lang = isZh ? 'zh' : 'en';
    return nodes
      .filter((n) => n.isActive && !excluded.has(n.id))
      .sort((a, b) => a.path.localeCompare(b.path))
      .map((n) => ({
        value: n.id,
        label: `${n.code} — ${buildBreadcrumb(nodes, n.id, lang)}`,
      }));
  }, [isZh, node, nodes]);

  const submit = async () => {
    if (!node) return;
    let invalid = false;
    if (!comment.trim()) {
      setShowCommentError(true);
      invalid = true;
    }
    if (replacementRequired && !replacementId) {
      setShowReplacementError(true);
      invalid = true;
    }
    if (invalid) return;

    setSaving(true);
    try {
      const response = await deactivateNode(node.id, {
        replacementId,
        comment: comment.trim(),
      });
      message.success(
        response.reassignedProducts > 0
          ? t('taxonomy:deactivate.reassigned', { count: response.reassignedProducts })
          : t('taxonomy:deactivate.success')
      );
      onClose();
      onDeactivated();
    } catch (error) {
      const guard = extractGuardError(error);
      if (guard === 'REPLACEMENT_REQUIRED') {
        // Server says products are assigned in the subtree — escalate
        setReplacementRequired(true);
        setShowReplacementError(true);
        return;
      }
      message.error(guard ? t(`taxonomy:errors.${guard}`) : t('common:messages.updateFailed'));
    } finally {
      setSaving(false);
    }
  };

  const nodeName = node ? (isZh ? node.nameZh : node.nameEn) : '';

  return (
    <Modal
      open={Boolean(node)}
      title={t('taxonomy:deactivate.title', { name: nodeName })}
      okText={t('taxonomy:deactivate.action')}
      cancelText={t('common:actions.cancel')}
      onCancel={onClose}
      onOk={submit}
      confirmLoading={saving}
      width={520}
      okButtonProps={{
        danger: true,
        style: { height: `${controlHeightTokens.controlHeightXL}px` },
      }}
      cancelButtonProps={{ style: { height: `${controlHeightTokens.controlHeightXL}px` } }}
      destroyOnHidden
    >
      <Text style={{ display: 'block', marginBottom: token.marginMD }}>
        {t('taxonomy:deactivate.warning')}
      </Text>

      {replacementRequired && (
        <Alert
          type="warning"
          showIcon
          message={t('taxonomy:deactivate.productsAssigned')}
          style={{ marginBottom: token.marginMD }}
        />
      )}

      <Text style={formStyles.labelStyle}>
        {replacementRequired && (
          <span style={{ color: token.colorError, marginRight: '4px' }}>*</span>
        )}
        {t('taxonomy:fields.replacement')}
      </Text>
      <Select
        size="large"
        showSearch
        allowClear
        optionFilterProp="label"
        value={replacementId}
        onChange={(value) => {
          setReplacementId(value);
          if (value) setShowReplacementError(false);
        }}
        options={replacementOptions}
        placeholder={t('taxonomy:deactivate.replacementHint')}
        style={formStyles.inputStyle}
        status={showReplacementError ? 'error' : undefined}
      />
      {showReplacementError && (
        <Text style={formStyles.errorStyle}>{t('taxonomy:validation.replacementRequired')}</Text>
      )}

      <Text style={{ ...formStyles.labelStyle, marginTop: '16px' }}>
        <span style={{ color: token.colorError, marginRight: '4px' }}>*</span>
        {t('taxonomy:fields.comment')}
      </Text>
      <Input.TextArea
        rows={3}
        value={comment}
        onChange={(event) => {
          setComment(event.target.value);
          if (event.target.value.trim()) setShowCommentError(false);
        }}
        placeholder={t('taxonomy:detail.commentPlaceholder')}
        style={{ ...formStyles.inputStyle, height: 'auto' }}
        status={showCommentError ? 'error' : undefined}
      />
      {showCommentError && (
        <Text style={formStyles.errorStyle}>{t('taxonomy:validation.commentRequired')}</Text>
      )}
    </Modal>
  );
};

export default DeactivateNodeModal;
